import React from "react";
import PropTypes from "prop-types";
import { ReactReduxContext } from "react-redux";
import { Box, Typography, Chip, Button } from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { capitalizeFirstLetter } from "../../utils/capitalize";
import { formatDateTime } from "../../utils/formatDateTime";
import { getNameVariants, maskAllButLast } from "../../utils/maskedData";

const formatSecondsToHMS = (totalSeconds) => {
  if (totalSeconds == null || Number.isNaN(totalSeconds) || totalSeconds < 0) {
    return "00:00:00";
  }
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (num) => String(num).padStart(2, "0");
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const getMemberFields = (opportunity) => ({
  memberName: opportunity?.name || opportunity?.member?.name || "John Doe",
  memberId:
    opportunity?.subscriberNum ||
    opportunity?.member?.subscriberNum ||
    opportunity?.member?.memberId ||
    opportunity?.memberId ||
    "BCBS-1089263",
  personCode: opportunity?.member?.personCode || opportunity?.personCode || "",
  carrier: capitalizeFirstLetter(
    opportunity?.member?.carrierName ||
      opportunity?.carrierName ||
      opportunity?.carrier ||
      "",
  ),
  groupNameRaw: opportunity?.member?.groupName || opportunity?.groupName || "",
  groupId:
    opportunity?.member?.groupNum ||
    opportunity?.member?.groupId ||
    opportunity?.groupNum ||
    opportunity?.groupId ||
    "",
});

const getMemberDisplayFields = (opportunity, isShowPhi) => {
  const { memberName, memberId, personCode, carrier, groupNameRaw, groupId } =
    getMemberFields(opportunity);

  return {
    displayMemberName: memberName
      ? getNameVariants(memberName, isShowPhi).maskedMiddle
      : "",
    displayMemberId: memberId ? maskAllButLast(memberId, 2, isShowPhi) : "N/A",
    personCode,
    carrier,
    groupName: groupNameRaw ? capitalizeFirstLetter(groupNameRaw) : "",
    groupId,
  };
};

const useReduxPhiToggle = () => {
  const reduxContext = React.useContext(ReactReduxContext);
  return React.useSyncExternalStore(
    reduxContext?.store ? reduxContext.store.subscribe : () => () => {},
    reduxContext?.store
      ? () => reduxContext.store.getState()?.toggles?.phi ?? false
      : () => false,
    () => false,
  );
};

const useTimerDisplay = ({ startTime, readOnly, elapsedTimeProp }) => {
  const [timerDisplay, setTimerDisplay] = React.useState("");

  React.useEffect(() => {
    if (elapsedTimeProp !== undefined && elapsedTimeProp !== null) {
      if (readOnly) {
        setTimerDisplay("");
      } else {
        const numSeconds =
          typeof elapsedTimeProp === "number"
            ? elapsedTimeProp
            : Number.parseInt(elapsedTimeProp, 10);
        setTimerDisplay(formatSecondsToHMS(numSeconds));
      }
      return undefined;
    }

    if (!startTime || readOnly) {
      setTimerDisplay("");
      return undefined;
    }

    const updateTimer = () => {
      const fixedStart = String(startTime).endsWith("Z")
        ? String(startTime)
        : String(startTime) + "Z";
      const start = new Date(fixedStart);
      const now = new Date();
      const diffMs = now - start;
      if (Number.isNaN(diffMs) || diffMs < 0) {
        setTimerDisplay("00:00:00");
        return;
      }
      const totalSeconds = Math.floor(diffMs / 1000);
      setTimerDisplay(formatSecondsToHMS(totalSeconds));
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [startTime, readOnly, elapsedTimeProp]);
  return timerDisplay;
};

const MemberDetails = ({
  opportunity,
  startTime,
  completionTime,
  readOnly,
  isDraft,
  elapsedTime: elapsedTimeProp,
  isShowPhiEnabled: isShowPhiEnabledProp,
  onExportPdf,
}) => {
  const reduxPhiToggle = useReduxPhiToggle();
  const isShowPhi =
    isShowPhiEnabledProp !== undefined ? isShowPhiEnabledProp : !reduxPhiToggle;

  const timerDisplay = useTimerDisplay({
    startTime,
    readOnly,
    elapsedTimeProp,
  });
  const {
    displayMemberName,
    displayMemberId,
    personCode,
    carrier,
    groupName,
    groupId,
  } = getMemberDisplayFields(opportunity, isShowPhi);

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "15px",
        p: 2,
        border: "1px solid #E8E8E9",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* Member Name & Drug Info */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{ fontWeight: 600, fontSize: "18px", color: "#101828" }}
          >
            {displayMemberName}
          </Typography>
          {isDraft && (
            <Chip
              label="Draft"
              size="small"
              sx={{
                backgroundColor: "#FFF3CD",
                color: "#856404",
                fontWeight: 600,
                fontSize: "11px",
                height: "18px",
                borderRadius: "4px",
              }}
            />
          )}
        </Box>

        {/* Info Rows */}
        <Typography
          sx={{
            fontSize: "12px",
            color: "#4A5565",
            display: "flex",
            gap: "4px",
            alignItems: "center",
          }}
        >
          <span>
            Member ID:{" "}
            <span style={{ fontWeight: 600 }}>{displayMemberId}</span>
          </span>
          <span>•</span>
          <span>
            Person Code:{" "}
            <span style={{ fontWeight: 600 }}>{personCode || "N/A"}</span>
          </span>
        </Typography>

        <Typography
          sx={{
            fontSize: "12px",
            color: "#4A5565",
            display: "flex",
            gap: "4px",
            alignItems: "center",
          }}
        >
          <span>
            Carrier: <span style={{ fontWeight: 600 }}>{carrier || "N/A"}</span>
          </span>
          <span>•</span>
          <span>
            Group Name:{" "}
            <span style={{ fontWeight: 600 }}>{groupName || "N/A"}</span>
          </span>
          <span>•</span>
          <span>
            Group ID:{" "}
            <span style={{ fontWeight: 600 }}>{groupId || "N/A"}</span>
          </span>
        </Typography>
      </Box>

      {/* Session Start Time & Live Timer */}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
        {timerDisplay && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: "#ECFDF3",
              borderRadius: "8px",
              px: 2,
              py: 1,
              border: "1px solid #D1FADF",
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "12px",
                color: "#039855",
                letterSpacing: "0.5px",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#34D399",
                }}
              ></span>
              {"Elapsed: "}
              <span style={{ color: "#027A48" }}>{timerDisplay}</span>
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            backgroundColor: "#F9FAFB",
            borderRadius: "8px",
            px: 2,
            py: 1,
            border: "1px solid #EAECF0",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "12px",
              color: "#475467",
              letterSpacing: "0.5px",
            }}
          >
            Started At:{" "}
            <span style={{ color: "#101828" }}>
              {formatDateTime(startTime, { showSeconds: true })}
            </span>
          </Typography>
        </Box>
        {(completionTime || onExportPdf) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 1,
            }}
          >
            {completionTime && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  backgroundColor: "#ECFDF3",
                  borderRadius: "8px",
                  px: 2,
                  py: 1,
                  border: "1px solid #D1FADF",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "12px",
                    color: "#039855",
                    letterSpacing: "0.5px",
                  }}
                >
                  Completed At:{" "}
                  <span style={{ color: "#027A48" }}>
                    {formatDateTime(completionTime, { showSeconds: true })}
                  </span>
                </Typography>
              </Box>
            )}
            {onExportPdf && (
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  <PictureAsPdfOutlinedIcon
                    sx={{ fontSize: "16px !important" }}
                  />
                }
                onClick={onExportPdf}
                sx={{
                  borderRadius: "50px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "12px",
                  color: "#403C6F",
                  borderColor: "#403C6F",
                  px: 2,
                  py: 0.5,
                  "&:hover": {
                    backgroundColor: "rgba(64, 60, 111, 0.06)",
                    borderColor: "#312e5e",
                  },
                }}
              >
                Export PDF
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

MemberDetails.propTypes = {
  opportunity: PropTypes.object,
  startTime: PropTypes.string,
  completionTime: PropTypes.string,
  readOnly: PropTypes.bool,
  isDraft: PropTypes.bool,
  elapsedTime: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isShowPhiEnabled: PropTypes.bool,
  onExportPdf: PropTypes.func,
};

export default MemberDetails;
